// Security review: bind sessions and asynchronous work to a live account/network generation.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createWalletClient, createPublicClient, custom, http } from 'viem';
import { Api, ApiError, api, onSessionExpired } from './api.js';
import { sessionBoundary, readWalletIdentity, sameIdentity, sessionMatchesWallet } from './session-boundary.js';
import { sendWalletIntent } from './transactions.js';
import { contracts as mainnetContracts } from '../../../../contracts/deployments/opbnb.json';

export const WalletState = Object.freeze({
  NO_PROVIDER: 'no_provider', DISCONNECTED: 'disconnected', CONNECTING: 'connecting',
  CONNECTION_REJECTED: 'connection_rejected', CONNECTED: 'connected',
  SIGNATURE_REQUIRED: 'signature_required', SIGNATURE_REJECTED: 'signature_rejected',
  AUTHENTICATED: 'authenticated', SESSION_EXPIRED: 'session_expired',
  WRONG_NETWORK: 'wrong_network', NETWORK_SWITCHING: 'network_switching',
  NETWORK_SWITCH_REJECTED: 'network_switch_rejected', RPC_UNAVAILABLE: 'rpc_unavailable',
  UNSUPPORTED_WALLET: 'unsupported_wallet',
});
const WalletContext = createContext(null);
export const EXPECTED_CHAIN_ID = Number(import.meta.env?.VITE_CHAIN_ID ?? 204);
const chain = {
  id: EXPECTED_CHAIN_ID, name: 'opBNB',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: { default: { http: [import.meta.env?.VITE_RPC_URL ?? 'https://opbnb-mainnet-rpc.bnbchain.org'] } },
};

export function WalletProvider({ children }) {
  const [state, setState] = useState(WalletState.DISCONNECTED);
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionEpoch, setSessionEpoch] = useState(sessionBoundary.epoch);
  const [isGuest, setIsGuest] = useState(false);
  const walletRef = useRef(null);
  const sessionRef = useRef(null);
  const identityRef = useRef({ address: null, chainId: null });
  const authPendingRef = useRef(null);
  const foreignSessionRef = useRef(false);
  const provider = typeof window !== 'undefined' ? window.ethereum : undefined;
  const publicClient = useMemo(() => createPublicClient({ chain, transport: http(chain.rpcUrls.default.http[0]) }), []);

  const connectedState = useCallback((identity) => {
    if (!provider) return WalletState.NO_PROVIDER;
    if (!provider.request) return WalletState.UNSUPPORTED_WALLET;
    if (!identity.address) return WalletState.DISCONNECTED;
    return identity.chainId === EXPECTED_CHAIN_ID ? WalletState.CONNECTED : WalletState.WRONG_NETWORK;
  }, [provider]);

  const setIdentity = useCallback((identity) => {
    identityRef.current = identity;
    setAddress(identity.address);
    setChainId(identity.chainId);
    walletRef.current = identity.address && provider?.request
      ? createWalletClient({ chain, transport: custom(provider), account: identity.address }) : null;
  }, [provider]);

  const invalidate = useCallback(() => {
    const epoch = sessionBoundary.invalidate();
    setSessionEpoch(epoch);
    sessionRef.current = null;
    setSession(null);
    setIsGuest(false);
    setSessionReady(false);
    return epoch;
  }, []);

  const acceptSession = useCallback((result, epoch, identity) => {
    sessionBoundary.assertCurrent(epoch);
    const guest = result.isGuest === true || !result.walletAddress;
    if (!guest && !sessionMatchesWallet(result, identity, EXPECTED_CHAIN_ID)) throw new Error('Session does not match the connected wallet');
    const next = { ...result, isGuest: guest };
    sessionRef.current = next;
    setSession(next);
    setIsGuest(guest);
    setState(guest ? connectedState(identity) : WalletState.AUTHENTICATED);
    sessionBoundary.activate(epoch, result.ownerId);
    foreignSessionRef.current = false;
    setSessionReady(true);
  }, [connectedState]);

  const resetSession = useCallback((identity, { logout = true, nextState = connectedState(identity) } = {}) => {
    const epoch = invalidate();
    setIdentity(identity);
    setState(nextState);
    return sessionBoundary.serialize(async () => {
      // Logout executes even when another switch superseded this reset: an
      // earlier verify may have set a cookie after the invalidation event.
      if (logout) {
        try { await Api.logout(); }
        catch (err) { if (err.status !== 401) throw err; }
      }
      if (!sessionBoundary.current(epoch)) return;
      const guest = await Api.guest();
      if (!sessionBoundary.current(epoch)) return;
      if (!guest.isGuest) throw new Error('Previous wallet session could not be cleared');
      acceptSession(guest, epoch, identityRef.current);
      if (nextState === WalletState.SESSION_EXPIRED) setState(nextState);
    }).catch((err) => {
      if (!sessionBoundary.current(epoch)) return;
      setError(err.message);
      setState(WalletState.RPC_UNAVAILABLE);
      // Keep owner data blocked until the old cookie is definitely revoked.
    });
  }, [invalidate, setIdentity, connectedState, acceptSession]);

  useEffect(() => {
    const epoch = invalidate();
    // A provider lookup must never occupy the cookie queue: a hung extension
    // must not prevent a disconnect from revoking the server session.
    (async () => {
      let identity;
      let identityError = null;
      try { identity = await readWalletIdentity(provider); }
      catch (err) { identity = { address: null, chainId: null }; identityError = err; }
      if (!sessionBoundary.current(epoch)) return;
      setIdentity(identity);
      await sessionBoundary.serialize(async () => {
        if (!sessionBoundary.current(epoch)) return;
        let restored;
        try { restored = await Api.session(); }
        catch (err) { if (err.status !== 401) throw err; restored = null; }
        if (!restored?.ownerId) restored = await Api.guest();
        if (!sessionBoundary.current(epoch)) return;
        if (restored.walletAddress && !sessionMatchesWallet(restored, identity, EXPECTED_CHAIN_ID)) {
          await Api.logout();
          if (!sessionBoundary.current(epoch)) return;
          restored = await Api.guest();
        }
        if (sessionBoundary.current(epoch)) {
          acceptSession(restored, epoch, identity);
          if (identityError) { setError(identityError.message); setState(WalletState.RPC_UNAVAILABLE); }
        }
      });
    })().catch((err) => {
      if (sessionBoundary.current(epoch)) { setError(err.message); setState(WalletState.RPC_UNAVAILABLE); }
    });
    return () => { sessionBoundary.invalidate(); };
  }, [provider, invalidate, setIdentity, acceptSession]);

  useEffect(() => {
    if (!provider?.on) return undefined;
    const change = (identity) => {
      if (sameIdentity(identityRef.current, identity)) return;
      const logout = Boolean(sessionRef.current?.walletAddress || authPendingRef.current || !sessionBoundary.ready);
      void resetSession(identity, { logout });
    };
    const onAccounts = (accounts) => change({ ...identityRef.current, address: accounts?.[0]?.toLowerCase() ?? null });
    const onChain = (value) => change({ ...identityRef.current, chainId: Number(value) });
    const onDisconnect = () => { void resetSession({ address: null, chainId: null }); };
    provider.on('accountsChanged', onAccounts);
    provider.on('chainChanged', onChain);
    provider.on('disconnect', onDisconnect);
    return () => {
      provider.removeListener?.('accountsChanged', onAccounts);
      provider.removeListener?.('chainChanged', onChain);
      provider.removeListener?.('disconnect', onDisconnect);
    };
  }, [provider, resetSession]);

  useEffect(() => onSessionExpired((code) => {
    if (code === 'SESSION_CHANGED') {
      invalidate();
      foreignSessionRef.current = true;
      setState(WalletState.RPC_UNAVAILABLE);
      setError('The session changed in another tab. Reconnect with the current wallet to continue.');
      return;
    }
    void resetSession(identityRef.current, { nextState: WalletState.SESSION_EXPIRED });
  }), [resetSession, invalidate]);

  useEffect(() => {
    if (!session?.expiresAt) return undefined;
    // setTimeout overflows above 2^31 ms; long-lived guest sessions renew the
    // check at that boundary, while wallet expiry clears authority immediately.
    let timer;
    const check = () => {
      const remaining = Date.parse(session.expiresAt) - Date.now();
      if (remaining <= 0) void resetSession(identityRef.current, { nextState: WalletState.SESSION_EXPIRED });
      else timer = setTimeout(check, Math.min(remaining, 2_147_483_647));
    };
    check();
    return () => clearTimeout(timer);
  }, [session, resetSession]);

  const connect = useCallback(async () => {
    setError(null);
    if (!provider?.request) { setState(provider ? WalletState.UNSUPPORTED_WALLET : WalletState.NO_PROVIDER); return; }
    setState(WalletState.CONNECTING);
    try {
      await provider.request({ method: 'eth_requestAccounts' });
      const identity = await readWalletIdentity(provider);
      if (!identity.address) throw new Error('Wallet did not expose an account');
      if (!sameIdentity(identityRef.current, identity) || !sessionBoundary.ready) {
        await resetSession(identity, { logout: Boolean(sessionRef.current?.walletAddress || authPendingRef.current || foreignSessionRef.current) });
      } else {
        setIdentity(identity);
        setState(sessionMatchesWallet(sessionRef.current, identity, EXPECTED_CHAIN_ID) ? WalletState.AUTHENTICATED : connectedState(identity));
      }
    } catch (err) {
      setError(err.message);
      setState(err.code === 4001 ? WalletState.CONNECTION_REJECTED : WalletState.RPC_UNAVAILABLE);
    }
  }, [provider, resetSession, setIdentity, connectedState]);

  const switchNetwork = useCallback(async () => {
    if (!provider?.request) return;
    setState(WalletState.NETWORK_SWITCHING);
    try {
      try {
        await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: `0x${EXPECTED_CHAIN_ID.toString(16)}` }] });
      } catch (err) {
        if (err.code !== 4902) throw err;
        await provider.request({ method: 'wallet_addEthereumChain', params: [{ chainId: `0x${EXPECTED_CHAIN_ID.toString(16)}`, chainName: chain.name, nativeCurrency: chain.nativeCurrency, rpcUrls: chain.rpcUrls.default.http }] });
        // Adding a chain does not guarantee the wallet switched to it.
        await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: `0x${EXPECTED_CHAIN_ID.toString(16)}` }] });
      }
      const identity = await readWalletIdentity(provider);
      await resetSession(identity, { logout: Boolean(sessionRef.current?.walletAddress || authPendingRef.current || foreignSessionRef.current) });
    } catch (err) { setError(err.message); setState(WalletState.NETWORK_SWITCH_REJECTED); }
  }, [provider, resetSession]);

  const assertIdentity = useCallback(async (epoch, expected) => {
    sessionBoundary.assertCurrent(epoch);
    const actual = await readWalletIdentity(provider);
    sessionBoundary.assertCurrent(epoch);
    if (!sameIdentity(actual, expected) || actual.chainId !== EXPECTED_CHAIN_ID) {
      void resetSession(actual);
      throw new Error('Wallet account or network changed. Sign in again.');
    }
  }, [provider, resetSession]);

  const signIn = useCallback(async () => {
    if (authPendingRef.current === sessionBoundary.epoch || !sessionBoundary.ready) return;
    const identity = identityRef.current;
    if (!identity.address || identity.chainId !== EXPECTED_CHAIN_ID) { setState(connectedState(identity)); return; }
    const epoch = invalidate();
    authPendingRef.current = epoch;
    let verificationStarted = false;
    setState(WalletState.SIGNATURE_REQUIRED);
    setError(null);
    try {
      await assertIdentity(epoch, identity);
      const { message, nonce } = await Api.challenge(identity.address);
      await assertIdentity(epoch, identity);
      const signature = await provider.request({ method: 'personal_sign', params: [message, identity.address] });
      await assertIdentity(epoch, identity);
      const result = await sessionBoundary.serialize(async () => {
        sessionBoundary.assertCurrent(epoch);
        verificationStarted = true;
        return Api.verify({ walletAddress: identity.address, nonce, signature });
      });
      await assertIdentity(epoch, identity);
      acceptSession(result, epoch, identity);
    } catch (err) {
      if (!sessionBoundary.current(epoch)) return;
      setError(err instanceof ApiError ? `${err.message} (${err.requestId ?? err.code})` : err.message);
      const recovery = resetSession(identity, { logout: verificationStarted });
      const recoveryEpoch = sessionBoundary.epoch;
      await recovery;
      if (sessionBoundary.current(recoveryEpoch) && err.code === 4001) setState(WalletState.SIGNATURE_REJECTED);
    } finally { if (authPendingRef.current === epoch) authPendingRef.current = null; }
  }, [provider, invalidate, assertIdentity, acceptSession, connectedState, resetSession]);

  // DEV ONLY: DEV is false in every production build, even if the bypass flag
  // was mistakenly left enabled. No privileged signer is ever part of this UI.
  const devSignIn = useCallback(async () => {
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_WALLET_BYPASS === 'true' && (EXPECTED_CHAIN_ID === 31337 || EXPECTED_CHAIN_ID === 1337)) {
      const epoch = invalidate();
      try {
        const result = await sessionBoundary.serialize(() => api('/v1/auth/dev-login', { json: {} }));
        sessionBoundary.assertCurrent(epoch);
        const identity = { address: result.walletAddress.toLowerCase(), chainId: EXPECTED_CHAIN_ID };
        setIdentity(identity);
        acceptSession(result, epoch, identity);
      } catch (err) { if (sessionBoundary.current(epoch)) { setError(err.message); setState(WalletState.DISCONNECTED); } }
    }
  }, [invalidate, setIdentity, acceptSession]);

  const signOut = useCallback(() => resetSession(identityRef.current), [resetSession]);
  const signOutEverywhere = useCallback(async () => {
    const epoch = invalidate();
    setState(connectedState(identityRef.current));
    try {
      return await sessionBoundary.serialize(async () => {
        sessionBoundary.assertCurrent(epoch);
        const result = await Api.logoutAll();
        if (!sessionBoundary.current(epoch)) return result;
        const guest = await Api.guest();
        if (sessionBoundary.current(epoch)) acceptSession(guest, epoch, identityRef.current);
        return result;
      });
    } catch (err) {
      if (sessionBoundary.current(epoch)) { setError(err.message); setState(WalletState.RPC_UNAVAILABLE); }
      throw err;
    }
  }, [invalidate, connectedState, acceptSession]);
  const promptConnect = useCallback(() => {
    if ([WalletState.CONNECTED, WalletState.SIGNATURE_REJECTED, WalletState.SESSION_EXPIRED].includes(state)) void signIn();
    else if ([WalletState.WRONG_NETWORK, WalletState.NETWORK_SWITCH_REJECTED].includes(state)) void switchNetwork();
    else void connect();
  }, [state, connect, switchNetwork, signIn]);

  const sendIntent = useCallback(async ({ intent, txType, agentId }) => {
    const epoch = sessionBoundary.epoch;
    const identity = identityRef.current;
    if (!sessionBoundary.ready || !walletRef.current || !sessionMatchesWallet(sessionRef.current, identity, EXPECTED_CHAIN_ID)) throw new Error('Connect and sign in with the current wallet first');
    return sendWalletIntent({ intent, txType, agentId, expectedChainId: EXPECTED_CHAIN_ID,
      wallet: walletRef.current, publicClient, api: Api,
      expectedContracts: EXPECTED_CHAIN_ID === 204 ? mainnetContracts : null,
      assertIdentity: () => assertIdentity(epoch, identity),
    });
  }, [publicClient, assertIdentity]);

  const signWorkflowAuthorization = useCallback(async (workflowId) => {
    const epoch = sessionBoundary.epoch;
    const identity = identityRef.current;
    if (!sessionBoundary.ready || !sessionMatchesWallet(sessionRef.current, identity, EXPECTED_CHAIN_ID)) throw new Error('Sign in with the current wallet first');
    const payload = await Api.authorizationPayload(workflowId);
    await assertIdentity(epoch, identity);
    if (Number(payload.typedData?.domain?.chainId) !== EXPECTED_CHAIN_ID || payload.typedData.domain.name !== 'CortisWorkflow') throw new Error('Invalid workflow signature domain');
    const signature = await provider.request({ method: 'eth_signTypedData_v4', params: [identity.address, JSON.stringify({
      ...payload.typedData,
      types: { ...payload.typedData.types, EIP712Domain: [
        { name: 'name', type: 'string' }, { name: 'version', type: 'string' }, { name: 'chainId', type: 'uint256' },
      ] },
    })] });
    await assertIdentity(epoch, identity);
    await Api.authorizeWorkflow(workflowId, { signature, authorizationHash: payload.authorizationHash });
    await assertIdentity(epoch, identity);
  }, [provider, assertIdentity]);

  const value = {
    state, address, chainId, session, error, provider: Boolean(provider), expectedChainId: EXPECTED_CHAIN_ID,
    sessionReady, sessionEpoch, isGuest,
    isAuthenticated: state === WalletState.AUTHENTICATED && sessionMatchesWallet(session, { address, chainId }, EXPECTED_CHAIN_ID),
    wrongNetwork: chainId !== null && chainId !== EXPECTED_CHAIN_ID,
    connect, switchNetwork, signIn, signOut, signOutEverywhere, sendIntent, publicClient, promptConnect, devSignIn, signWorkflowAuthorization,
  };
  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used inside a WalletProvider');
  return ctx;
}

/** Minimal fetch-on-mount hook with abort, loading and error states. */
export function useApi(fn, deps = [], { skip = false } = {}) {
  const { sessionEpoch } = useWallet();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null);
    if (skip) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, skip, sessionEpoch]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
